import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import integracaoService, { type Integracao, type IntegracaoExecucao } from '../services/integracaoService';
import Button from '../components/ui/Button';

const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: unknown; details?: unknown } } }).response?.data;
    if (typeof data?.error === 'string') {
      return typeof data.details === 'string' && data.details ? `${data.error}: ${data.details}` : data.error;
    }
  }
  return fallback;
};

const formatarData = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// Nomes amigáveis para as contagens que cada integração devolve no resumo.
// Chave desconhecida cai no fallback (camelCase -> palavras), então integração
// nova funciona sem mexer aqui.
const ROTULOS: Record<string, string> = {
  created: 'Criados',
  updated: 'Atualizados',
  skipped: 'Pulados',
  contratosCriados: 'Contratos criados',
  contratosAtualizados: 'Contratos atualizados',
  atosLegaisCriados: 'Atos legais criados',
  atosLegaisAtualizados: 'Atos legais atualizados',
  vinculosAtoLegalCriados: 'Vínculos de ato legal',
  councilorsCreated: 'Vereadores criados',
  councilorsUpdated: 'Vereadores atualizados',
  councilorsSkipped: 'Vereadores pulados',
  institutionsCreated: 'Instituições criadas',
  institutionsUpdated: 'Instituições atualizadas',
  institutionsSkipped: 'Instituições puladas',
  emendasCreated: 'Emendas criadas',
  emendasUpdated: 'Emendas atualizadas',
  emendasSkipped: 'Emendas puladas',
};

const rotulo = (chave: string) =>
  ROTULOS[chave] ?? chave.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const STATUS_ESTILO: Record<IntegracaoExecucao['status'], { texto: string; classe: string }> = {
  SUCESSO: { texto: 'Concluída', classe: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:ring-emerald-900' },
  FALHA: { texto: 'Falhou', classe: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-950 dark:text-red-300 dark:ring-red-900' },
  EM_EXECUCAO: { texto: 'Em execução', classe: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950 dark:text-sky-300 dark:ring-sky-900' },
};

const StatusChip: React.FC<{ status: IntegracaoExecucao['status'] }> = ({ status }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${STATUS_ESTILO[status].classe}`}>
    {STATUS_ESTILO[status].texto}
  </span>
);

const ResumoExecucao: React.FC<{ execucao: IntegracaoExecucao }> = ({ execucao }) => {
  if (execucao.status === 'FALHA') {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
        {execucao.erro ?? 'A sincronização falhou.'}
      </div>
    );
  }

  const contagens = Object.entries(execucao.resumo ?? {}).filter(([, valor]) => typeof valor === 'number') as [string, number][];
  const avisos = Array.isArray(execucao.resumo?.warnings) ? (execucao.resumo?.warnings as string[]) : [];
  if (contagens.length === 0 && avisos.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
      <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3 lg:grid-cols-4">
        {contagens.map(([chave, valor]) => (
          <div key={chave}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{rotulo(chave)}</p>
            <p className="mt-1 text-xl font-bold tabular-nums text-slate-800 dark:text-slate-100">{valor}</p>
          </div>
        ))}
      </div>
      {avisos.length > 0 && (
        <details className="mt-3 text-sm text-slate-600 dark:text-slate-300">
          <summary className="cursor-pointer font-semibold">{avisos.length} aviso(s)</summary>
          <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs">
            {avisos.map((aviso, index) => (
              <li key={index} className="border-t border-slate-200 pt-1 first:border-t-0 first:pt-0 dark:border-slate-700">{aviso}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const HistoricoExecucoes: React.FC<{ integracaoId: string }> = ({ integracaoId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['integracoes', integracaoId, 'historico'],
    queryFn: () => integracaoService.historico(integracaoId, 10),
  });

  if (isLoading) return <p className="mt-2 text-xs text-slate-500">Carregando…</p>;
  if (!data || data.length === 0) return <p className="mt-2 text-xs text-slate-500">Nenhuma execução registrada.</p>;

  return (
    <ul className="mt-2 divide-y divide-slate-200 text-xs dark:divide-slate-700">
      {data.map((execucao) => (
        <li key={execucao.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
          <span className="tabular-nums text-slate-700 dark:text-slate-200">{formatarData(execucao.iniciadoEm)}</span>
          <StatusChip status={execucao.status} />
          <span className="text-slate-500 dark:text-slate-400">
            {execucao.disparo === 'AGENDADO' ? 'Agendada' : `Manual${execucao.usuario ? ` · ${execucao.usuario}` : ''}`}
            {execucao.forceReimport ? ' · reimportação forçada' : ''}
          </span>
          {execucao.erro && <span className="text-red-600 dark:text-red-400">{execucao.erro}</span>}
        </li>
      ))}
    </ul>
  );
};

const thClass = 'px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400';

const IntegracaoRows: React.FC<{ integracao: Integracao }> = ({ integracao }) => {
  const queryClient = useQueryClient();
  const [forceReimport, setForceReimport] = useState(false);
  const [historicoAberto, setHistoricoAberto] = useState(false);
  const [resultado, setResultado] = useState<IntegracaoExecucao | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const sincronizar = useMutation({
    mutationFn: () => integracaoService.sincronizar(integracao.id, forceReimport),
    onMutate: () => { setResultado(null); setErro(null); },
    onSuccess: (execucao) => setResultado(execucao),
    onError: (err: unknown) => setErro(errorMessage(err, 'Não foi possível concluir a sincronização.')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['integracoes'] }),
  });

  const ultima = integracao.ultimaExecucao;
  const ocupada = sincronizar.isPending || integracao.emExecucao;
  const temDetalhe = historicoAberto || !!erro || !!resultado;

  return (
    <>
      <tr className="align-top">
        <td className="px-4 py-3">
          <p className="font-semibold text-slate-900 dark:text-slate-100">{integracao.nome}</p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Origem: {integracao.origem} · {integracao.agendamento ? `Automática: ${integracao.agendamento}` : 'Somente manual'}
          </p>
          {!integracao.configurada && (
            <span className="mt-1 inline-block rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:ring-amber-900">
              Credenciais não configuradas
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
          {ultima ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="tabular-nums">{formatarData(ultima.iniciadoEm)}</span>
              <StatusChip status={ultima.status} />
              <span className="text-xs text-slate-500 dark:text-slate-400">{ultima.disparo === 'AGENDADO' ? 'agendada' : 'manual'}</span>
            </div>
          ) : (
            <span className="text-slate-500">nunca executada</span>
          )}
        </td>
        <td className="px-4 py-3">
          {integracao.suportaForceReimport ? (
            <label
              className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
              title="Atualiza também os registros já importados com o valor atual da origem, em vez de só trazer os novos."
            >
              <input type="checkbox" checked={forceReimport} onChange={(event) => setForceReimport(event.target.checked)} />
              Forçar reimportação
            </label>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setHistoricoAberto((aberto) => !aberto)}
              className="text-xs font-semibold text-slate-600 underline-offset-2 hover:underline dark:text-slate-300"
            >
              {historicoAberto ? 'Ocultar histórico' : 'Histórico'}
            </button>
            <Button variant="primary" size="sm" onClick={() => sincronizar.mutate()} disabled={ocupada}>
              {ocupada ? 'Sincronizando...' : 'Sincronizar agora'}
            </Button>
          </div>
        </td>
      </tr>
      {temDetalhe && (
        <tr>
          <td colSpan={4} className="space-y-3 bg-slate-50/60 px-4 py-3 dark:bg-slate-800/30">
            {erro && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
                {erro}
              </div>
            )}
            {resultado && <ResumoExecucao execucao={resultado} />}
            {historicoAberto && <HistoricoExecucoes integracaoId={integracao.id} />}
          </td>
        </tr>
      )}
    </>
  );
};

// Aba "Integrações" dentro de Parâmetros do Sistema. A lista vem do backend:
// integração nova aparece aqui sem mudança de layout.
export const IntegracoesTab: React.FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['integracoes'],
    queryFn: integracaoService.listar,
    // Enquanto alguma roda (inclusive o agendamento), acompanha até terminar.
    refetchInterval: (query) => (query.state.data?.some((i) => i.emExecucao) ? 3000 : false),
  });

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="px-6 pb-3 pt-5">
        <h2 className="text-lg font-bold text-slate-950 dark:text-slate-100">Integrações</h2>
        <p className="mt-1 max-w-4xl text-xs leading-5 text-slate-500 dark:text-slate-400">
          Sistemas externos que alimentam o SIGEM. Cada integração pode ser sincronizada à parte; serve também de fallback
          quando o agendamento automático falhar.
        </p>
        {data && data.length > 0 && (
          <ul className="mt-2 max-w-4xl space-y-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {data.map((integracao) => (
              <li key={integracao.id}>
                <strong className="font-semibold">{integracao.nome}:</strong> {integracao.descricao}
              </li>
            ))}
          </ul>
        )}
      </div>
      {isLoading && <p className="px-6 pb-4 text-sm text-slate-500">Carregando integrações…</p>}
      {isError && <p className="px-6 pb-4 text-sm text-red-600">Não foi possível carregar as integrações.</p>}
      {data && data.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] text-sm">
            <thead className="border-y border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
              <tr>
                <th className={thClass}>Integração</th>
                <th className={thClass}>Última sincronização</th>
                <th className={thClass}>Opções</th>
                <th className={thClass} aria-label="Ações" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.map((integracao) => <IntegracaoRows key={integracao.id} integracao={integracao} />)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default IntegracoesTab;
